import React, { useMemo } from 'react'
// @ts-ignore
import TimeGrid from 'react-big-calendar/lib/TimeGrid'
import { Navigate } from 'react-big-calendar'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
dayjs.extend(weekOfYear)

/**
 * @typedef {Object} CustomWeekProps
 * @property {Date} date
 * @property {any} localizer
 * @property {Date} max
 * @property {Date} min
 * @property {Date} scrollToTime
 */

/**
 * Custom week days range.
 * @param {CustomWeekProps} props
 * @return {JSX.Element}
 */
function CustomWeek({
  date,
  localizer,
  max = localizer.endOf(new Date(), 'day'),
  min = localizer.startOf(new Date(), 'day'),
  scrollToTime = localizer.startOf(new Date(), 'day'),
  ...moreProps
}) {
  const currRange = useMemo(() => CustomWeek.range(date, { localizer }), [date, localizer])

  return (
    <TimeGrid
      date={date}
      eventOffset={15}
      localizer={localizer}
      max={max}
      min={min}
      range={currRange}
      scrollToTime={scrollToTime}
      {...moreProps}
    />
  )
}

/**
 * Compose custom week visible days range.
 * @param {Date} date Current calendar date
 * @param {any} localizer
 * @return {Date[]}
 */
CustomWeek.range = (date, { localizer }) => {
  const firstOfWeek = localizer.startOfWeek()
  const start = localizer.startOf(date, 'week', firstOfWeek)
  const end = localizer.endOf(date, 'week', firstOfWeek)

  /**
   * @type {Date[]}
   */
  const weekRange = localizer.range(start, end)

  // Filter working days.
  return weekRange.filter((d) => [6, 0].indexOf(d.getDay()) === -1)
}

/**
 * Custom week previous/next navigation date computation.
 * @param {Date} date
 * @param {string} action
 * @param {any} localizer
 * @return {Date}
 */
CustomWeek.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -7, 'day')

    case Navigate.NEXT:
      return localizer.add(date, 7, 'day')

    default:
      return date
  }
}

/**
 * Custom week main title composition.
 * @param {Date} date
 * @return {string}
 */
CustomWeek.title = (date) => {
  const dateObject = dayjs(date.toISOString())
  const year = dateObject.format('YYYY')
  const weekNumber = dateObject.week()
  const formattedDate = `${year} - W${weekNumber.toString().padStart(2, '0')}`
  return `${formattedDate} (STT)`
}

export default CustomWeek
